"use client";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  Link,
  Stack,
  styled,
  SvgIconProps,
  Typography,
} from "@mui/material";

import {
  CategoryNode,
  NewCategoryDocument,
  NewContentItemDocument,
  ContentItem,
  RemoveContentItemDocument,
  RemoveCategoryDocument,
  UpdateCategoryNameDocument,
  UpdateContentItemRolesDocument,
  GetCategoryStructureDocument,
} from "@quales/data-access/gql-server";
import { useTranslations } from "next-intl";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FilePdfBox from "mdi-material-ui/FilePdfBox";
import {
  SimpleTreeView,
  TreeItem,
  TreeItemProps,
  treeItemClasses,
} from "@mui/x-tree-view";
import React from "react";
import { useWindowDimensions } from "@quales/utils/hooks";
import { CustomDialog, CustomModal } from "../ui/modal";
import { CustomTextField } from "../ui/input";
import { ModalButtonBar } from "../ui/button";
import { ISPermittedServer } from "@quales/utils/helpers";
import ContentForm, { ContentFormState } from "../forms/ContentForm";
import { CustomTypographyComponent } from "../ui/text";
import { useTheme } from "@quales/utils/theme";
import { AppContext } from "@quales/utils/context";
import RoleList from "./RoleList";
import { UserRoles } from "../utils/const";
import { useMutation, useQuery } from "@apollo/client";
import { Header } from "../ui/header";
import {
  PiShieldCheckeredFill,
  PiTrashSimpleFill,
  PiUploadSimple,
  PiFolderSimpleFill,
  PiPencilSimpleFill,
  PiFolderPlusLight,
} from "react-icons/pi";
import { enqueueSnackbar } from "notistack";

type StyledTreeItemProps = TreeItemProps & {
  bgColor?: string;
  color?: string;
  labelIcon: React.ElementType<SvgIconProps>;
  labelInfo?: string;
  labelText: string;
};

const StyledTreeItemRoot = styled(TreeItem)(({ theme }) => ({
  color: theme.palette.text.secondary,
  [`& .${treeItemClasses.content}`]: {
    color: theme.palette.text.secondary,
    borderTopRightRadius: theme.spacing(2),
    borderBottomRightRadius: theme.spacing(2),
    paddingRight: theme.spacing(1),
    fontWeight: theme.typography.fontWeightMedium,
    "&.Mui-expanded": {
      fontWeight: theme.typography.fontWeightRegular,
    },
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
    "&.Mui-focused, &.Mui-selected, &.Mui-selected.Mui-focused": {
      backgroundColor: `var(--tree-view-bg-color, ${theme.palette.action.selected})`,
    },
    [`& .${treeItemClasses.label}`]: {
      fontWeight: "inherit",
      color: "inherit",
    },
  },
}));

const StyledTreeItem = (props: StyledTreeItemProps) => {
  const {
    labelIcon: LabelIcon,
    labelInfo,
    labelText,

    ...other
  } = props;
  const { localAppState } = React.useContext(AppContext);
  const t = useTranslations();
  const theme = useTheme();

  const [hover, setHover] = React.useState(false);
  const actionsAllowed = ISPermittedServer(
    "content.manage",
    localAppState.userData
  );
  const [catName, setCatName] = React.useState(labelText || "");
  const [showConfirmDialog, setShowConfirmDialog] = React.useState(false);
  const [showRemoveContentFileDialog, setShowRemoveContentFileDialog] =
    React.useState(false);

  // const { tour } = React.useContext(TourContext);
  // const [tourRunning, setTourRunning] = React.useState(false);

  // React.useEffect(() => {
  //   if (tour) {
  //     setTourRunning(true);
  //   }
  // }, [tour]);
  // const handleTour = (tourID: string) => {
  //   switch (tourID) {
  //     case "2":
  //       return <UserTour />;

  //     default:
  //       return <></>;
  //   }
  // };

  const handleOnContentRemovedCompleted = function () {
    setTimeout(() => {
      enqueueSnackbar(t("feedback.removeSucess"), {
        variant: "success",
      });
      setHover(false);
      setShowRemoveContentFileDialog(false);
    }, 1000);
  };

  const handleOnContentRenamedCompleted = function () {
    setTimeout(() => {
      enqueueSnackbar(t("feedback.updateSucess"), {
        variant: "success",
      });
      setHover(false);
      setShowConfirmDialog(false);
    }, 1000);
  };

  const [removeCategory] = useMutation(RemoveCategoryDocument, {
    onCompleted: handleOnContentRemovedCompleted,
    refetchQueries: [
      {
        query: GetCategoryStructureDocument,
      },
    ],
  });

  const [editCateogry] = useMutation(UpdateCategoryNameDocument, {
    onCompleted: handleOnContentRenamedCompleted,
    refetchQueries: [
      {
        query: GetCategoryStructureDocument,
      },
    ],
  });

  const removeContentSelectItem = () => {
    setShowRemoveContentFileDialog(true);
  };

  const onCateogryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCatName(event.target.value);
  };

  const handleHideDialog = () => {
    setShowRemoveContentFileDialog(false);
  };

  const showCateogryDialog = () => {
    setShowConfirmDialog(true);
  };

  const handleHideConfirmDialog = () => {
    setShowConfirmDialog(false);
  };

  const handleRenameCategory = () => {
    editCateogry({
      variables: {
        input: {
          categoryID: props.itemId,
          name: catName,
        },
      },
    });
  };

  const handleConfirmDialog = () => {
    removeCategory({
      variables: {
        categoryID: props.itemId,
      },
    });
  };

  return (
    <StyledTreeItemRoot
      onMouseEnter={() => {
        setHover(true);
      }}
      onMouseLeave={() => {
        setHover(false);
      }}
      label={
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            p: 0.5,
            pr: 0,
            height: 40,
          }}
        >
          <Box component={LabelIcon} pr={4} />
          <Typography
            variant="body2"
            sx={{ fontWeight: "inherit", flexGrow: 1 }}
          >
            {labelText}
          </Typography>
          <Typography variant="caption" color="inherit">
            {labelInfo}
          </Typography>

          {hover && actionsAllowed && (
            <Stack direction={"row"} ml={2}>
              <IconButton
                onClick={() => showCateogryDialog()}
                style={{ padding: 6 }}
              >
                <PiPencilSimpleFill
                  color={theme.appColors.primary}
                  title={t("labels.edit")}
                  fontSize={16}
                />
              </IconButton>

              <IconButton
                onClick={() => removeContentSelectItem()}
                style={{ padding: 6 }}
              >
                <PiTrashSimpleFill
                  title={t("labels.deleteItem")}
                  fontSize={16}
                  color={theme.appColors.primary}
                />
              </IconButton>
            </Stack>
          )}
          {/* {tourRunning && handleTour(tour.tourID)} */}

          {showRemoveContentFileDialog && (
            <CustomDialog
              open={showRemoveContentFileDialog}
              leftButtonHeading={t("common.cancel")}
              rightButtonHeading={t("common.confirm")}
              handleCancel={handleHideDialog}
              handleClosePressed={handleConfirmDialog}
              heading={t("common.removeCategory")}
            ></CustomDialog>
          )}

          {showConfirmDialog && (
            <CustomDialog
              open={showConfirmDialog}
              leftButtonHeading={t("common.cancel")}
              rightButtonHeading={t("common.confirm")}
              handleCancel={handleHideConfirmDialog}
              handleClosePressed={handleRenameCategory}
              heading={t("common.editContentCategory")}
            >
              <FormControl sx={{ width: 300 }}>
                <CustomTextField
                  controldisabled={false}
                  id="filled-adornment-cateogry"
                  value={catName}
                  label={t("labels.categoryName")}
                  onChange={onCateogryChange}
                />
              </FormControl>
            </CustomDialog>
          )}
        </Box>
      }
      {...other}
    />
  );
};

export const ContentOverviewComposite = () => {
  const { height } = useWindowDimensions();
  const theme = useTheme();
  const t = useTranslations();
  const [removeContentFileID, setRemoveContentFileID] = React.useState("");
  const [showRemoveContentFileDialog, setShowRemoveContentFileDialog] =
    React.useState(false);
  const [currentContentItem, setCurrentContentItem] =
    React.useState<ContentItem>();
  const [currentRoles, setCurrentRoles] = React.useState<any[]>();
  const [showConfirmDialog, setShowConfirmDialog] = React.useState(false);
  const [showUpdateRoleDialog, setShowUpdateRoleDialog] = React.useState(false);
  const createRef = React.useRef();
  const [showUploadModal, setShowUploadModal] = React.useState(false);
  const [selectedNode, setSelectedNode] = React.useState<CategoryNode | null>(
    null
  );
  const [categoryList, setCategoryList] = React.useState<CategoryNode[]>([]);

  const {
    data: categoryStructureData,
    loading: categoryStructureLoading,
    refetch,
  } = useQuery(GetCategoryStructureDocument, {
    fetchPolicy: "network-only",
  });
  const { localAppState } = React.useContext(AppContext);
  const actionsAllowed = ISPermittedServer(
    "content.manage",
    localAppState.userData
  );

  React.useEffect(() => {
    if (
      !categoryStructureLoading &&
      categoryStructureData &&
      categoryStructureData.CategoryStructure &&
      categoryStructureData.CategoryStructure.length > 0
    ) {
      setCategoryList(categoryStructureData.CategoryStructure);
    } else {
      setCategoryList([]);
    }
  }, [categoryStructureData, categoryStructureLoading]);

  const handleCategorySelect = (catItem: CategoryNode) => {
    setSelectedNode(catItem);
    if (!selectedNode || catItem.categoryID !== selectedNode.categoryID) {
    }
  };

  const onCloseItem = () => {
    setShowConfirmDialog(false);
  };
  const onCloseUpdateRoleItem = () => {
    setShowUpdateRoleDialog(false);
    setCurrentContentItem(null);
    setCurrentRoles([]);
  };

  const handleShowModalPressed = () => {
    setShowConfirmDialog(true);
  };

  const handleShowUploadModalPressed = () => {
    setShowUploadModal(true);
  };

  const onCateogryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCategoryName(event.target.value);
  };

  const [cateogryName, setCategoryName] = React.useState("");

  const handleOnMutationCompleted = function () {
    setTimeout(() => {
      refetch();
      enqueueSnackbar(t("feedback.createSucess"), {
        variant: "success",
      });
      setShowConfirmDialog(false);
      setCategoryName("");
    }, 1000);
  };

  const [newCategory] = useMutation(NewCategoryDocument, {
    onCompleted: handleOnMutationCompleted,
  });

  const handleOnContentCompleted = function () {
    setTimeout(() => {
      refetch();
      enqueueSnackbar(t("feedback.createSucess"), {
        variant: "success",
      });
      setShowUploadModal(false);
    }, 1000);
  };

  const handleOnUpdateContentCompleted = function () {
    setTimeout(() => {
      refetch();
      enqueueSnackbar(t("feedback.createSuccess"), {
        variant: "success",
      });
      setCurrentRoles([]);
      setCurrentContentItem(null);
      setShowUpdateRoleDialog(false);
    }, 1000);
  };

  const handleOnContentRemovedCompleted = function () {
    setTimeout(() => {
      refetch();
      enqueueSnackbar(t("feedback.removeSucess"), {
        variant: "success",
      });
      setRemoveContentFileID("");
      setShowRemoveContentFileDialog(false);
    }, 1000);
  };

  const [newContentItem] = useMutation(NewContentItemDocument, {
    onCompleted: handleOnContentCompleted,
  });

  const [updateContentItemRole] = useMutation(UpdateContentItemRolesDocument, {
    onCompleted: handleOnUpdateContentCompleted,
  });

  const [removeContentItem] = useMutation(RemoveContentItemDocument, {
    onCompleted: handleOnContentRemovedCompleted,
  });

  const onCreateCategory = () => {
    newCategory({
      variables: {
        input: cateogryName,
      },
    });
  };

  const updateContentItemRoleFunction = () => {
    updateContentItemRole({
      variables: {
        input: {
          itemID: currentContentItem.itemID,
          roles: currentRoles.map((roleItem) => roleItem.id),
        },
      },
    });
  };
  const showUploadModalClosed = () => {
    setShowUploadModal(false);
  };

  const removeContentSelectItem = (itemID: string) => {
    setRemoveContentFileID(itemID);
    setShowRemoveContentFileDialog(true);
  };

  const updateRoleItem = (item: ContentItem) => {
    setCurrentContentItem(item);
    setShowUpdateRoleDialog(true);

    const localArray: any[] = [];

    item.roles.forEach((element: any) => {
      const subItem = UserRoles.find(
        (role: any) => role.id.toString() === element.toString()
      );

      if (item) {
        localArray.push(subItem);
      }
    });

    setCurrentRoles(localArray);
  };

  const handleHideDialog = () => {
    setShowRemoveContentFileDialog(false);
  };
  const handleConfirmDialog = () => {
    removeContentItem({
      variables: {
        itemID: removeContentFileID,
      },
    });
  };

  const handleFormSubmit = () => {
    if (createRef && createRef.current) {
      //@ts-ignore
      createRef.current.onSubmit();
    }
  };

  const handleNewContentItem = (values: ContentFormState, roles: any) => {
    const contractedRoles = roles.map((val) => val.id);

    newContentItem({
      variables: {
        catID: selectedNode.categoryID,
        input: {
          downloadURL: values.downLoadURL,
          mimeType: values.mimeType,
          name: values.name,
          description: values.description,
          roles: contractedRoles,
        },
      },
    });
  };
  const handleRoleUpdate = (roles: any) => {
    setCurrentRoles(roles);
  };

  return (
    <Stack id="ContentOverviewComposite">
      <Header
        pageTitle={t("common.contentOverview")}
        pageTitleTypographyProps={{
          variant: "h5",
          color: theme.appColors.primary,
        }}
      >
        <Stack direction="row" spacing={2} justifyContent="end" width={"100%"}>
          {actionsAllowed && (
            <>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleShowModalPressed}
                startIcon={<PiFolderPlusLight />}
              >
                {t("labels.newCategory")}
              </Button>

              <Button
                variant="outlined"
                color="secondary"
                onClick={handleShowUploadModalPressed}
                startIcon={<PiUploadSimple />}
                disabled={!selectedNode}
              >
                {t("labels.uploadPDF")}
              </Button>
            </>
          )}
        </Stack>
      </Header>

      <Stack
        spacing={2}
        paddingRight={3}
        overflow={"auto"}
        height={height - theme.dimensions.totalTopbarHeight}
      >
        {/* <Paper elevation={0}> */}
        <SimpleTreeView
          aria-label="view content"
          slots={{
            expandIcon: ChevronRightIcon,
            collapseIcon: ExpandMoreIcon,
          }}
          sx={{ flexGrow: 1, overflowY: "auto" }}
        >
          {!categoryStructureLoading &&
            categoryList &&
            categoryList.length > 0 &&
            categoryList.map((cat: CategoryNode) => (
              <StyledTreeItem
                onClick={(event: any) => {
                  if (
                    !selectedNode ||
                    cat.categoryID !== selectedNode.categoryID
                  ) {
                    handleCategorySelect(cat);
                  }
                }}
                itemId={cat.categoryID.toString()}
                key={cat.categoryID}
                labelIcon={PiFolderSimpleFill}
                labelText={cat.name}
                labelInfo={
                  cat.items && cat.items.length > 0
                    ? cat.items.length.toString()
                    : "0"
                }
              >
                {cat.items &&
                  cat.items.length > 0 &&
                  cat.items.map((item: ContentItem) => (
                    <Stack
                      key={item.itemID + cat.categoryID}
                      padding={1}
                      marginLeft={10}
                      sx={{
                        backgroundColor: theme.appColors.surfaceContainerLow,
                      }}
                    >
                      <Stack
                        direction={"row"}
                        alignItems={"center"}
                        justifyContent={"center"}
                      >
                        <Stack width="95%">
                          <Link
                            sx={{
                              "&:hover": {
                                backgroundColor: "transparent",
                              },
                            }}
                            href={
                              "/files/fileDownloadUrl/" +
                              item.downloadUrl +
                              "?storageType=content"
                            }
                            target="_blank"
                            rel="noreferrer"
                          >
                            <TreeItem
                              sx={{
                                "&:hover": {
                                  background: "none",
                                },
                              }}
                              key={item.itemID + cat.categoryID + item.name}
                              itemId={
                                item.itemID.toString() +
                                cat.categoryID.toString() +
                                item.name
                              }
                              label={item.name}
                              slots={{
                                icon: FilePdfBox,
                              }}
                              slotProps={{
                                iconContainer: {
                                  color: theme.appColors.onPrimary,
                                },
                                icon: {
                                  color: theme.appColors.error,
                                },
                              }}
                            />
                          </Link>
                        </Stack>
                        <Stack
                          width={"15%"}
                          direction={"row"}
                          alignSelf={"flex-end"}
                        >
                          {actionsAllowed && (
                            <>
                              <IconButton onClick={() => updateRoleItem(item)}>
                                <PiShieldCheckeredFill
                                  title={t("labels.updateRoles")}
                                  color={theme.appColors.primary}
                                  fontSize={16}
                                />
                              </IconButton>

                              <IconButton
                                onClick={() =>
                                  removeContentSelectItem(
                                    item.itemID.toString()
                                  )
                                }
                              >
                                <PiTrashSimpleFill
                                  title={t("labels.delete")}
                                  color={theme.appColors.primary}
                                  fontSize={16}
                                />
                              </IconButton>
                            </>
                          )}
                        </Stack>
                      </Stack>

                      <Stack marginLeft={10}>
                        {item && item.description && (
                          <CustomTypographyComponent fontSize={14}>
                            {t("labels.description")}: {item.description}
                          </CustomTypographyComponent>
                        )}
                      </Stack>
                    </Stack>
                  ))}
              </StyledTreeItem>
            ))}
        </SimpleTreeView>
        {/* </Paper> */}
      </Stack>

      {showConfirmDialog && (
        <CustomDialog
          open={showConfirmDialog}
          leftButtonHeading={t("common.cancel")}
          rightButtonHeading={t("common.confirm")}
          handleCancel={onCloseItem}
          handleClosePressed={onCreateCategory}
          heading={t("common.contentCategory")}
        >
          <FormControl sx={{ width: 300 }}>
            <CustomTextField
              controldisabled={false}
              id="filled-adornment-cateogry"
              value={cateogryName}
              label={t("labels.updateFolderTitle")}
              onChange={onCateogryChange}
            />
          </FormControl>
        </CustomDialog>
      )}

      {showUpdateRoleDialog && (
        <CustomDialog
          open={showUpdateRoleDialog}
          leftButtonHeading={t("common.cancel")}
          rightButtonHeading={t("confirm")}
          handleCancel={onCloseUpdateRoleItem}
          handleClosePressed={updateContentItemRoleFunction}
          heading={t("common.updateRole")}
        >
          <FormControl sx={{ width: 300 }}>
            <RoleList
              width={250}
              edit={true}
              error={false}
              InputPropsValue={currentRoles}
              onValueChanged={handleRoleUpdate}
            />
          </FormControl>
        </CustomDialog>
      )}

      {showRemoveContentFileDialog && (
        <CustomDialog
          open={showRemoveContentFileDialog}
          leftButtonHeading={t("common.cancel")}
          rightButtonHeading={t("common.confirm")}
          handleCancel={handleHideDialog}
          handleClosePressed={handleConfirmDialog}
          heading={t("common.removeContentItem")}
        ></CustomDialog>
      )}

      {showUploadModal && (
        <CustomModal
          modalTitle={t("common.addNewContentItem")}
          openModal={showUploadModal}
          closeHandler={showUploadModalClosed}
        >
          <Stack width={"100%"} height={250}>
            <Stack direction={"row"} justifyContent="flex-end">
              <ModalButtonBar
                disabled={false}
                onEdit={null}
                isCreate={true}
                onSave={handleFormSubmit}
              />
            </Stack>
            <Stack
              height={"100%"}
              width={600}
              direction={"column"}
              spacing={2}
              sx={{ overflowX: "none", overflowY: "auto" }}
            >
              <ContentForm
                ref={createRef}
                onContentDetails={handleNewContentItem}
              />
            </Stack>
          </Stack>
        </CustomModal>
      )}
    </Stack>
  );
};

export default ContentOverviewComposite;
